// Builds the clientMeta payload sent with the login request. Captures the
// maximum a browser sandbox exposes: screen resolution, IANA timezone, locale,
// platform string, network type (NOT SSID — never exposed), a stable
// fingerprint hash, and a best-effort WebRTC LAN-IP probe.
//
// Everything is best-effort. If any sub-step throws, we ship whatever we have.

const LOCAL_IP_PROBE_TIMEOUT_MS = 500;

export async function collectClientMeta() {
  const screenStr =
    (window.screen?.width && window.screen?.height)
      ? `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`
      : null;

  let timezone = null;
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (_) {}

  const locale = navigator.language || navigator.userLanguage || null;
  const platform = navigator.platform || null;
  const connectionType = navigator.connection?.type || navigator.connection?.effectiveType || null;

  const fingerprintHash = await safeFingerprint();
  const localIps = await safeLocalIps();
  const platformVersion = await safePlatformVersion();

  return {
    screen: screenStr,
    timezone,
    locale,
    platform,
    platformVersion,
    connectionType,
    fingerprintHash,
    localIps
  };
}

// The ONLY way to tell Windows 11 from Windows 10 — both report "Windows NT 10.0"
// in the User-Agent string, so the server can't distinguish them without this.
// Chromium-only (userAgentData is not implemented in Firefox or Safari); those
// browsers resolve to null and the server records the OS without a version.
async function safePlatformVersion() {
  try {
    const uaData = navigator.userAgentData;
    if (!uaData?.getHighEntropyValues) return null;
    const hints = await uaData.getHighEntropyValues(['platformVersion']);
    return hints?.platformVersion || null;
  } catch (_) {
    return null;
  }
}

async function safeFingerprint() {
  try {
    const parts = [
      navigator.userAgent,
      navigator.platform,
      navigator.language,
      navigator.hardwareConcurrency ?? '',
      navigator.deviceMemory ?? '',
      window.screen?.width ?? '',
      window.screen?.height ?? '',
      window.screen?.colorDepth ?? '',
      Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    ].join('|');
    if (!window.crypto?.subtle) return null;
    const enc = new TextEncoder().encode(parts);
    const hash = await window.crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return null;
  }
}

// Best-effort LAN-IP probe via WebRTC. Modern Chrome/Firefox/Safari obfuscate
// host candidates as mDNS `.local` hostnames — most logins will return nothing
// useful here, but the few that leak a real RFC1918 address get captured.
// Hard-capped at 500ms so login latency is never impacted.
function safeLocalIps() {
  return new Promise((resolve) => {
    if (typeof RTCPeerConnection === 'undefined') return resolve(null);
    let pc;
    const found = new Set();
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      try { pc?.close(); } catch (_) {}
      resolve(found.size > 0 ? Array.from(found).slice(0, 5) : null);
    };

    try {
      pc = new RTCPeerConnection({ iceServers: [] });
      pc.createDataChannel('p');
      pc.onicecandidate = (e) => {
        if (!e.candidate) return finish();
        const m = e.candidate.candidate.match(/candidate:\S+ \d+ \S+ \d+ (\S+) /);
        if (m && m[1]) {
          const v = m[1];
          // Skip mDNS placeholders — they're useless for audit.
          if (!/\.local$/.test(v)) found.add(v);
        }
      };
      pc.createOffer().then(o => pc.setLocalDescription(o)).catch(finish);
    } catch (_) {
      return finish();
    }

    setTimeout(finish, LOCAL_IP_PROBE_TIMEOUT_MS);
  });
}
